import React from 'react'
import { useAuth } from '@contexts/AuthContext'
import { getLevelConfig } from './data/levelData'
import LevelPath from './components/LevelPath'
import './PartnerMap.css'

const PartnerMapPage: React.FC = () => {
  const { user } = useAuth()
  const currentLevel = user?.level || 0
  const currentLevelData = getLevelConfig(currentLevel)

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="max-w-7xl mx-auto">
        {/* 標題區域 */}
        <div className="relative bg-gradient-to-r from-primary-500 to-primary-600 rounded-3xl shadow-2xl border-2 border-primary-200 overflow-hidden mb-12">
          {/* 背景裝飾 */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none"></div>
          
          <div className="relative p-8 md:p-12 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-shadow-lg">
              🗺️ 夥伴地圖
            </h1>
            <p className="text-lg md:text-xl opacity-95 font-medium mb-8">
              和一群有趣的人一起做有趣的事情。把工作變有趣是我們很重視的能力
            </p>
            
            {/* 當前用戶職等卡片 */}
            <div className="inline-block bg-white/15 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-6 shadow-xl">
              <div className="text-sm opacity-85 mb-2 font-medium">您目前的職等</div>
              <div className="flex items-center justify-center gap-4">
                {/* 當前等級徽章 */}
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center
                  font-bold text-sm text-white border-3 border-white/80
                  ${currentLevelData.colorClass.includes('gradient') 
                    ? currentLevelData.colorClass 
                    : `${currentLevelData.colorClass} bg-gradient-to-br ${currentLevelData.gradientClass}`
                  }
                  animate-pulse shadow-lg
                `}>
                  LV{currentLevel}
                </div>
                <div>
                  <div className="text-xl font-bold">{currentLevelData.name}</div>
                  <div className="text-sm opacity-90">時薪 NT$ {currentLevelData.hourlyRate}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 地圖容器 */}
        <div className="relative bg-white rounded-3xl shadow-xl border-2 border-primary-100 p-8 md:p-12">
          <LevelPath currentUserLevel={currentLevel} />
        </div>

        {/* 圖例說明 */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl border-2 border-primary-100 p-8 md:p-12">
          <h3 className="text-2xl font-bold text-primary-700 text-center mb-8">
            🎯 職等階段說明
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* 初級階段 */}
            <div className="flex items-center gap-3 p-4 bg-primary-50 rounded-xl border border-primary-200">
              <div className="w-6 h-6 rounded-full bg-gray-500 border-2 border-gray-600 flex-shrink-0"></div>
              <span className="font-medium text-gray-700">初級階段<br /><small className="text-gray-500">(LV0-LV1)</small></span>
            </div>
            
            {/* 專業階段 */}
            <div className="flex items-center gap-3 p-4 bg-primary-50 rounded-xl border border-primary-200">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 border-2 border-amber-600 flex-shrink-0"></div>
              <span className="font-medium text-gray-700">專業階段<br /><small className="text-gray-500">(LV2-LV4)</small></span>
            </div>
            
            {/* 高級階段 */}
            <div className="flex items-center gap-3 p-4 bg-primary-50 rounded-xl border border-primary-200">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 border-2 border-purple-600 flex-shrink-0"></div>
              <span className="font-medium text-gray-700">高級階段<br /><small className="text-gray-500">(LV5-LV7)</small></span>
            </div>
            
            {/* 傳奇階段 */}
            <div className="flex items-center gap-3 p-4 bg-primary-50 rounded-xl border border-primary-200">
              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-red-500 via-purple-500 to-blue-500 border-2 border-purple-500 flex-shrink-0 animate-pulse"></div>
              <span className="font-medium text-gray-700">傳奇階段<br /><small className="text-gray-500">(LV8-LV9)</small></span>
            </div>
            
            {/* 合夥人 */}
            <div className="flex items-center gap-3 p-4 bg-primary-50 rounded-xl border border-primary-200">
              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 border-2 border-amber-500 flex-shrink-0 animate-pulse"></div>
              <span className="font-medium text-gray-700">合夥人<br /><small className="text-gray-500">(LV10)</small></span>
            </div>
          </div>

          {/* 使用說明 */}
          <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200">
            <h4 className="text-lg font-bold text-blue-800 mb-3">💡 使用說明</h4>
            <div className="space-y-2 text-sm text-blue-700">
              <p>• <strong>職等介紹</strong> 移動滑鼠查看詳細的升等條件和福利制度</p>
              <p>• <strong>升等方法</strong> 所有升等需符合上述評核條件，可自行提交主理人進行升等申請，每個月可以申請1次</p>
              <p>• <strong>升等評估</strong> 每週工作品質分數可能納入考量</p>
              <p>• <strong>使用補助</strong> 各項補助皆需提供相關證明申請</p>
              <p>• <strong>績效獎金</strong> 績效獎金依每月考核結果通知後核發</p>
              <p>• <strong>特別注意</strong> 連續超過12個月未升等，則會降1級</p>
              <p>• <strong>公告聲明</strong> 恆遠數位行銷保留夥伴規則最後修改權利</p>
            </div>
          </div>

          {/* 鼓勵文字 */}
          <div className="mt-8 text-center">
            <div className="inline-block p-4 bg-gradient-to-r from-primary-100 to-amber-100 rounded-2xl border border-primary-200">
              <p className="text-primary-700 font-medium">
                🚀 我們喜歡和一群人一起學習、一起成長、一起進步、一起變成更好的人✨
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PartnerMapPage
